import { useAuth } from "@clerk/nextjs";
import { useCallback } from "react";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    throw new ApiError(res.status, body.error || res.statusText);
  }
  return res.json();
}

async function getAuthHeaders(getToken: () => Promise<string | null>): Promise<HeadersInit> {
  let token: string | null = null;
  try {
    token = await getToken();
  } catch {
    // Clerk may be offline in dev
  }
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export function useApi() {
  const { getToken } = useAuth();

  const authHeaders = useCallback(async (): Promise<HeadersInit> => {
    return getAuthHeaders(getToken);
  }, [getToken]);

  const get = useCallback(
    async <T>(path: string): Promise<T> => {
      const headers = await authHeaders();
      const res = await fetch(path, { headers });
      return handleResponse<T>(res);
    },
    [authHeaders],
  );

  const post = useCallback(
    async <T>(path: string, body?: unknown): Promise<T> => {
      const headers = await authHeaders();
      const res = await fetch(path, {
        method: "POST",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      return handleResponse<T>(res);
    },
    [authHeaders],
  );

  const put = useCallback(
    async <T>(path: string, body?: unknown): Promise<T> => {
      const headers = await authHeaders();
      const res = await fetch(path, {
        method: "PUT",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      return handleResponse<T>(res);
    },
    [authHeaders],
  );

  const patch = useCallback(
    async <T>(path: string, body?: unknown): Promise<T> => {
      const headers = await authHeaders();
      const res = await fetch(path, {
        method: "PATCH",
        headers,
        body: body ? JSON.stringify(body) : undefined,
      });
      return handleResponse<T>(res);
    },
    [authHeaders],
  );

  const del = useCallback(
    async (path: string): Promise<void> => {
      const headers = await authHeaders();
      const res = await fetch(path, { method: "DELETE", headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: res.statusText }));
        throw new ApiError(res.status, body.error || res.statusText);
      }
    },
    [authHeaders],
  );

  return { get, post, put, patch, del, getToken };
}

export function createFetcher(getToken: () => Promise<string | null>) {
  return async <T>(url: string): Promise<T> => {
    const headers = await getAuthHeaders(getToken);
    const res = await fetch(url, { headers });
    return handleResponse<T>(res);
  };
}

export async function fetchWithoutAuth<T>(url: string): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
  });
  return handleResponse<T>(res);
}
